import { useMutation } from "@apollo/client";
import { useModal } from "@ebay/nice-modal-react";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import CodeEditor from "components/CodeEditor";
import FormFieldTags from "components/FormFields/Tags";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import { PluginTemplateCreateInput } from "types/generated";
import { getDrawerFormFix } from "utils/css";
import { validateUniqueResourceName } from "utils/formValidators";

import { CREATE_PLUGIN_TEMPLATE } from "./gql";

type FormValues = {
  description: string;
  labels: Record<"value", string>[];
  manifest: string;
  name: string;
};

type DefaultValues = {
  description: string;
  labels: string[];
  manifest: string;
  name: string;
};

type PluginTemplateCreateDrawerProps = {
  takenPluginNames: string[];
  defaultValues?: DefaultValues;
};

const PluginTemplateCreateDrawer = createDrawer(
  ({ takenPluginNames, defaultValues }: PluginTemplateCreateDrawerProps) => {
    const drawer = useModal();
    const closeDrawer = () => {
      drawer.resolve();
      drawer.hide();
    };
    const [createPlugin, { loading }] = useMutation(CREATE_PLUGIN_TEMPLATE, {
      onCompleted: (data) => {
        if (data?.pluginTemplateCreate?.id) {
          closeDrawer();
        }
      },
      refetchQueries: ["GetPluginTemplates"],
    });

    const createPluginForm = useForm<FormValues>({
      defaultValues: {
        name: defaultValues?.name ?? "",
        labels: defaultValues?.labels.map((value) => ({ value })) ?? [],
        description: defaultValues?.description ?? "",
        manifest: defaultValues?.manifest ?? "",
      },
      mode: "onChange",
    });

    const {
      formState: { errors, isDirty },
      handleSubmit,
      register,
      watch,
    } = createPluginForm;

    const handleCreatePlugin = (data: FormValues) => {
      const input: PluginTemplateCreateInput = {
        ...data,
        labels: data.labels.map((label) => label.value),
      };
      createPlugin({
        variables: {
          input,
        },
      });
    };

    const manifest = watch("manifest");

    const namesToValidate = useMemo(
      () => takenPluginNames.map((value) => value.toLowerCase()),
      [takenPluginNames]
    );

    return (
      <DrawerForm isDirty={isDirty}>
        <FormProvider {...createPluginForm}>
          <form onSubmit={handleSubmit(handleCreatePlugin)} {...getDrawerFormFix()}>
            <DrawerHeader justify="between">
              <DrawerHeaderTitle title="Create a new plugin" />
              <DrawerCloseIcon />
            </DrawerHeader>
            <DrawerBody fullHeight gap="large">
              <FormField label="Name" error={errors?.name?.message} noMargin>
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Name of your new plugin"
                    error={!!errors?.name}
                    {...register("name", {
                      setValueAs: (value) => value.trim(),
                      validate: validateUniqueResourceName(
                        namesToValidate,
                        undefined,
                        "A plugin template with this name already exists in the account"
                      ),
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormField
                label="Description"
                isOptional
                error={errors?.description?.message}
                noMargin
              >
                {({ ariaInputProps }) => (
                  <Textarea
                    placeholder="Enter policy description here..."
                    error={!!errors?.description}
                    {...register("description")}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormFieldTags name="labels" tagName="label" label="Labels" isOptional noMargin />

              <FormField label="Manifest" error={errors?.manifest?.message} noMargin fullHeight>
                {({ ariaInputProps }) => (
                  <CodeEditor
                    body={manifest}
                    language="yaml"
                    onChange={(value?: string) =>
                      createPluginForm.setValue("manifest", value ?? "")
                    }
                    options={{ automaticLayout: true }}
                    rounded
                    {...ariaInputProps}
                  />
                )}
              </FormField>
            </DrawerBody>
            <DrawerFooter>
              <DrawerFooterActions>
                <DrawerCancelButton />

                <Button variant="primary" type="submit" disabled={loading} loading={loading}>
                  Create
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </form>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showPluginTemplateCreateDrawer = createDrawerTrigger(PluginTemplateCreateDrawer);
